import React, { useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Services
import { createRouter } from './components/_pages/_routes';
import { AuthProvider } from './providers/auth.provider';
import { UserProvider } from './providers/user/user.provider';
import { RecommendationProvider } from './providers/recommendations/recommendation.provider';
import { SnackBarProvider } from './components/Snackbar/useSnackBar';
import { OnboardingProvider } from './providers/onboarding/onboarding.provider';
import { AdditionalInfoProvider } from './providers/additional-information.provider';
import { LocaleProvider } from './providers/locale.provider';
import { LocaleUserSync } from './providers/locale-user-sync';
import { LocationProvider } from './providers/location.provider';
import { LocationAddressSync } from './providers/location-address-sync';
import { LocationLocaleSync } from './providers/location-locale-sync';

// Components
import GlobalStyle, { theme } from './components/styles/GlobalStyle';
import { useLiveUpdates } from './hooks/useLiveUpdates';

if (Capacitor.isNativePlatform()) {
  try {
    Keyboard.setAccessoryBarVisible({ isVisible: false });
  } catch {}
  try {
    Keyboard.setScroll({ isDisabled: false });
  } catch {}
}

const Main: React.FC = () => {
  const router = useMemo(() => createRouter(), []);
  useLiveUpdates(router);

  return (
    <>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <RouterProvider router={router} />
      </ThemeProvider>
    </>
  );
};

const App: React.FC = () => {
  return (
    <LocaleProvider>
      <LocationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AuthProvider>
            <UserProvider>
              <LocaleUserSync />
              <LocationLocaleSync />
              <OnboardingProvider>
                <LocationAddressSync />
                <SnackBarProvider>
                  <RecommendationProvider>
                    <AdditionalInfoProvider>
                      <Main />
                    </AdditionalInfoProvider>
                  </RecommendationProvider>
                </SnackBarProvider>
              </OnboardingProvider>
            </UserProvider>
          </AuthProvider>
        </LocalizationProvider>
      </LocationProvider>
    </LocaleProvider>
  );
};

export default App;
